export type AbilityId = 'speed' | 'shield' | 'magnet' | 'double_jump' | 'time_slow' | 'health_regen'

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary'

export interface CharacterAbility {
  id: AbilityId
  name: string
  description: string
  icon: string
  color: string
  effectDescription: string
}

export const CHARACTER_ABILITIES: Record<AbilityId, CharacterAbility> = {
  speed: {
    id: 'speed',
    name: 'Speed Boost',
    description: 'Slides travel noticeably faster.',
    icon: 'lightning',
    color: '#FFD700',
    effectDescription: 'Slide animation speed +15%',
  },
  shield: {
    id: 'shield',
    name: 'Guardian Shield',
    description: 'Start every attempt with a free shield.',
    icon: 'shield',
    color: '#4CAF50',
    effectDescription: 'One hazard hit absorbed per attempt',
  },
  magnet: {
    id: 'magnet',
    name: 'Coin Magnet',
    description: 'Automatically collect nearby coins when you stop.',
    icon: 'magnet',
    color: '#FF9800',
    effectDescription: 'Auto-collects coins within 1 tile of each stop',
  },
  double_jump: {
    id: 'double_jump',
    name: 'Phase Echo',
    description: 'A shimmering after-image trails every slide.',
    icon: 'wings',
    color: '#2196F3',
    effectDescription: 'Cosmetic phase trail during slides',
  },
  time_slow: {
    id: 'time_slow',
    name: 'Chrono Control',
    description: 'Slides play out slower so routes are easier to read.',
    icon: 'clock',
    color: '#9C27B0',
    effectDescription: 'Permanent slow-motion slide readability',
  },
  health_regen: {
    id: 'health_regen',
    name: 'Auto Recovery',
    description: 'Earn a bonus to route score for steady play.',
    icon: 'heart',
    color: '#E91E63',
    effectDescription: 'Score +10% on every resolved run',
  },
}

export interface CharacterPalette {
  base: string
  emissive: string
  accent: string
}

export interface CharacterShape {
  bodyScale: number
  eyeScale: number
  eyeGap: number
  mouthScale: number
  emblem: 'heart' | 'star' | 'spark'
}

export interface CharacterDef {
  id: string
  name: string
  description: string
  rarity: Rarity
  price?: { coins?: number; gems?: number }
  unlockLevel?: number
  iapOnly?: boolean
  abilities: AbilityId[]
  palette: CharacterPalette
  shape: CharacterShape
}

export const CHARACTERS: CharacterDef[] = [
  {
    id: 'default_hero',
    name: 'Ember',
    description: 'The warm little glow that started it all. Balanced and reliable.',
    rarity: 'common',
    abilities: ['speed'],
    palette: { base: '#ffb454', emissive: '#ffd27a', accent: '#ff8f3f' },
    shape: { bodyScale: 1, eyeScale: 1, eyeGap: 1, mouthScale: 1, emblem: 'heart' },
  },
  {
    id: 'tiny_blue',
    name: 'Velocity',
    description: 'Compact speedster built for precision and agility.',
    rarity: 'rare',
    unlockLevel: 5,
    abilities: ['speed', 'double_jump'],
    palette: { base: '#4a90e2', emissive: '#5ba0f2', accent: '#2968a3' },
    shape: { bodyScale: 0.82, eyeScale: 1.18, eyeGap: 0.95, mouthScale: 0.95, emblem: 'spark' },
  },
  {
    id: 'chonky_green',
    name: 'Guardian',
    description: 'Heavily armored tank with defensive capabilities.',
    rarity: 'epic',
    unlockLevel: 15,
    abilities: ['shield', 'health_regen'],
    palette: { base: '#4caf50', emissive: '#66bb6a', accent: '#1b5e20' },
    shape: { bodyScale: 1.16, eyeScale: 0.62, eyeGap: 1.3, mouthScale: 1.3, emblem: 'heart' },
  },
  {
    id: 'sleek_purple',
    name: 'Mystic',
    description: 'Mysterious wanderer with temporal abilities.',
    rarity: 'epic',
    unlockLevel: 25,
    abilities: ['time_slow', 'magnet'],
    palette: { base: '#9c27b0', emissive: '#ab47bc', accent: '#4a148c' },
    shape: { bodyScale: 0.88, eyeScale: 0.78, eyeGap: 0.9, mouthScale: 0.85, emblem: 'star' },
  },
  {
    id: 'golden_glow',
    name: 'Luminary',
    description: 'Radiant collector with enhanced treasure-finding abilities.',
    rarity: 'legendary',
    unlockLevel: 40,
    price: { gems: 100 },
    abilities: ['magnet', 'speed', 'double_jump'],
    palette: { base: '#ffc107', emissive: '#ffca28', accent: '#ff6f00' },
    shape: { bodyScale: 0.96, eyeScale: 1, eyeGap: 1, mouthScale: 1, emblem: 'star' },
  },
  {
    id: 'aurora_luminary',
    name: 'Aurora Luminary',
    description: 'Prestige aurora form of the Luminary. Exclusive to the store.',
    rarity: 'legendary',
    iapOnly: true,
    abilities: ['magnet', 'speed', 'time_slow'],
    palette: { base: '#e56aff', emissive: '#9ff0ff', accent: '#6a3bff' },
    shape: { bodyScale: 1.02, eyeScale: 1.08, eyeGap: 1, mouthScale: 1, emblem: 'star' },
  },
  {
    id: 'keeper',
    name: 'The Keeper',
    description: 'Crowned frost sentinel carrying a warm lantern core through the cold.',
    rarity: 'legendary',
    unlockLevel: 60,
    price: { gems: 150 },
    abilities: ['shield', 'time_slow', 'health_regen'],
    palette: { base: '#274b70', emissive: '#ff9f32', accent: '#75e4ff' },
    shape: { bodyScale: 1.14, eyeScale: 0.76, eyeGap: 1.2, mouthScale: 1.15, emblem: 'star' },
  },
]

export function getCharacter(id: string): CharacterDef | undefined {
  return CHARACTERS.find((c) => c.id === id)
}

export const RARITY_COLORS: Record<Rarity, string> = {
  common: '#9E9E9E',
  rare: '#4a90e2',
  epic: '#9c27b0',
  legendary: '#ffc107',
}
